import React, { useEffect, useRef, useState, useCallback } from 'react';
import type { Dashboard,Sheet, Workbook } from '@tableau/embedding-api';
import {
  CrosstabFileFormat,
  FilterUpdateType,
  TableauEventType,
  TableauViz,
  Toolbar,
} from '@tableau/embedding-api';
import { Box } from '@mui/material';
import Button1 from '@mui/material/Button';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import { useSelector } from 'react-redux';
import CommonModal from './ExportReportModal';
import ExportMenu from './ExportMenu';
import FilterDrawer from './FilterDrawer';
import type { RootState } from '../../store/store';
import AppliedFilters from './AppliedFilters';
import Loader from '../Loader';
import SaveReportModal from './SaveReportModal';
import { useSaveFilter } from '../../hooks/useSaveFilter';
import { useLogin } from 'src/hooks/useLogin';
import GradientButton from '../MUICustomButton/GradientButton';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import OutlineButton from '../MUICustomButton/OutlineButton';
import type {
  logsearchdata,
} from '../types/tableauFilter';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import { NotificationManager } from 'react-notifications';
interface Filter {
  _isAllSelected: boolean;
  _fieldName: string;
  _appliedValues: { _formattedValue: string }[];
}

interface TableauSheet extends Sheet {
  getWorksheetsAsync?: () => Promise<TableauSheet[]>;
  exportCrossTabToExcelAsync?: () => Promise<string>;
}

interface TableauWorkbookExtended extends Workbook {
  activeSheet: TableauSheet;
}

interface TableauVizExtended {
  workbook: TableauWorkbookExtended;
  exportImageAsync: () => Promise<string>;
}

const TableauEmbedded: React.FC = () => {
  const vizRef = useRef<HTMLDivElement | null>(null);
  const vizInstance = useRef<TableauViz | null>(null);
  const [filters, setFilters] = useState<Filter[]>([]);
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>(
    {},
  );

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [worksheetNames, setWorksheetNames] = useState<string[]>([]);
  const [showWorksheetModal, setShowWorksheetModal] = useState(false);
 
  const [appliedFilters, setAppliedFilters] = useState<
    Record<string, string[]>
  >({});
  const [filterSearch, setFilterSearch] = useState<string>('');
  const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
  const [existingReportNames, setExistingReportNames] = useState<string[]>([]);

  // const dispatch = useDispatch<AppDispatch>();
  const { tableauToken, user } = useSelector((state: RootState) => state.auth);
  const dashboardUrl = process.env.REACT_APP_DASHBOARD_URL ?? '';
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { saveFilter, logSearchdetails } = useSaveFilter();
  const { handleAuthenticationFailure } = useLogin();
  useEffect(() => {
    void handleAuthenticationFailure();
  }, []);
  useEffect(() => {
    if (vizRef.current && tableauToken !== null && !vizInstance.current) {
      setupTableauViz();
    }
  }, [tableauToken]);

  useEffect(() => {
    if (vizRef.current && tableauToken !== null && !vizInstance.current) {
      try {
        setupTableauViz();
      } catch (error) {
        console.error('Error setting up Tableau viz:', error);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        NotificationManager.error("Error setting up dashboard. Please try again.");
     //   setSnackbarMessage('Error setting up dashboard. Please try again.');
      }
    }
  }, [tableauToken]);
  const setupTableauViz = (): void => {
    if (
      vizRef.current &&
      tableauToken != null &&
      dashboardUrl !== '' &&
      dashboardUrl !== null
    ) {
      const viz = new TableauViz();
      viz.src = dashboardUrl;
      viz.toolbar = Toolbar.Hidden;
      viz.hideTabs = true;
      viz.token = tableauToken;
      viz.addEventListener(TableauEventType.FirstInteractive, () => {
        void handleFirstInteractive(viz);
      });
      vizRef.current.innerHTML = '';
      vizRef.current.appendChild(viz);
      vizInstance.current = viz;
    }
  };

  const handleFirstInteractive = async (viz: TableauViz): Promise<void> => {
    setIsLoading(true);
    const tableauViz = viz as unknown as {
      workbook: {
        activeSheet: {
          worksheets: { getFiltersAsync: () => Promise<Filter[]> }[];
        };
      };
    };
    const { workbook } = tableauViz;
    const { activeSheet } = workbook;
    const sheets = activeSheet.worksheets;
    console.log(sheets);

    try {
      const fetchedFilters = await sheets[0].getFiltersAsync();
      console.log(fetchedFilters);
      const processedFilters = fetchedFilters
        .filter(
          (filter) =>
            filter._fieldName !== 'Measure Names' &&
            filter._fieldName !== 'Action (State)' &&
            !filter._isAllSelected,
        )
        .map((filter) => ({
          ...filter,
          _appliedValues: filter._appliedValues
            .filter(
              (value) =>
                value._formattedValue != null && value._formattedValue !== '',
            )
            .sort((a, b) => a._formattedValue.localeCompare(b._formattedValue)),
        }));
      setFilters(processedFilters);
    } catch (error) {
      console.error('Error fetching filters:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (
    filterName: string,
    selectedValues: string[],
  ): void => {
    setFilterValues((prev) => ({
      ...prev,
      [filterName]: selectedValues,
    }));
  };

  const handleAddFilterClick = (): void => {
    setIsFilterDrawerOpen(true);
  };
  const logSearchActions = async (activityType : string,filterValues: Record<string, string[]> ): Promise<void> => {
    const userId = user?.id;
    const dashboardId = 1;
    const searchDetails = JSON.stringify(filterValues);
    const logsearchdata = {
      userId,
      dashboardId,
      searchDetails,
      activityType,
    };
    
    await logSearchdetails(logsearchdata as logsearchdata);

  }

  const applyFilters = async (): Promise<void> => {
    setIsLoading(true);
    if (vizRef.current) {
      const viz = vizRef.current.querySelector('tableau-viz') as unknown as {
        workbook: {
          activeSheet: {
            worksheets: {
              applyFilterAsync: (
                fieldName: string,
                values: string[],
                updateType: FilterUpdateType,
              ) => Promise<void>;
              clearFilterAsync: (fieldName: string) => Promise<void>;
            }[];
          };
        };
      } | null;

      if (viz) {
        const { workbook } = viz;
        const { activeSheet } = workbook;
        const sheets = activeSheet.worksheets;

            try {
               
                for (const filterName of Object.keys(filterValues)) {
                    await sheets[0].clearFilterAsync(filterName);
                }

              
                for (const [filterName, values] of Object.entries(filterValues)) {
                    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                    if (values && values.length > 0) {
                        await sheets[0].applyFilterAsync(
                            filterName,
                            values,
                            FilterUpdateType.Replace,
                        );
                    }
                }

              
                setAppliedFilters(filterValues);
                setIsFilterDrawerOpen(false); // Close the filter drawer after applying filters
                void logSearchActions('Apply Filter',filterValues);// To log the applied filter value

            } catch (error) {
                console.error('Error applying filters:', error);
                 // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                NotificationManager.error("Error applying filters!")

             //   setSnackbarMessage('Error applying filters!');
            } finally {
                setIsLoading(false); // Reset loading state
            }
        }
    }
  };

  const handleSaveFilters = (): void => {
    setShowSaveFilterModal(true);
  };

  const saveFilters = useCallback(
    async (reportName: string): Promise<void> => {
      if (!vizInstance.current || !user) {
        console.error('Viz instance or user not available');
        return;
      }
      try {
        const userId = user.id;
        const dashboardId = 1;
        const filtersJson = JSON.stringify(appliedFilters);
        const dashboardReportName = reportName;
        const dataToSave = {
          userId,
          dashboardId,
          filterData: filtersJson,
          dashboardReportName,
        };

        console.log('Data to save:', dataToSave);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        await saveFilter(dataToSave);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        NotificationManager.success("Filters saved successfully")

     //   setSnackbarMessage('Filters saved successfully');
        setShowSaveFilterModal(false);
        void logSearchActions('Save Applied Filter',appliedFilters); // To log the saved reports with applied filter value
        setExistingReportNames((prev) => [...prev, reportName]);
      } catch (error) {
        console.error('Error saving filters:', error);
       // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        NotificationManager.error("Error saving filters")

     //   setSnackbarMessage('Error saving filters');
      }
    },
    [appliedFilters, saveFilter, user, vizInstance],
  );

  const clearFilters = async (): Promise<void> => {
    setIsLoading(true);

    if (vizRef.current) {
      const viz = vizRef.current.querySelector('tableau-viz') as unknown as {
        workbook: {
          activeSheet: {
            worksheets: {
              clearFilterAsync: (fieldName: string) => Promise<void>;
            }[];
          };
        };
      } | null;

      if (viz) {
        const { workbook } = viz;
        const { activeSheet } = workbook;
        const sheets = activeSheet.worksheets;

        try {
          const clearFilterPromises = Object.keys(appliedFilters).map(
            (filterName) => sheets[0].clearFilterAsync(filterName),
          );

          await Promise.all(clearFilterPromises);

                setFilterValues({});
                setAppliedFilters({});
                void logSearchActions('Clear all Applied Filter',appliedFilters); 
            } catch (error) {
                console.error('Error clearing filters:', error);
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                NotificationManager.error("Error clearing filters!")

             //   setSnackbarMessage('Error clearing filters!');
            } finally {
                setIsLoading(false);
            }
        }
    }
  };

  const handleExport = (exportType: string): void => {
    if (vizRef.current) {
      const viz = vizRef.current.querySelector(
        'tableau-viz',
      ) as unknown as TableauViz;
      const vizExtended: TableauVizExtended = {
        workbook: viz.workbook as TableauWorkbookExtended,
        exportImageAsync: viz.exportImageAsync.bind(
          viz,
        ) as unknown as () => Promise<string>,
      };

      switch (exportType) {
        case 'Image':
          void exportAsImage(vizExtended);
          break;
        case 'PDF':
          void exportAsPDF(viz);
          break;
        case 'CrossTab':
          void exportAsCrossTab(viz);
          break;
        default:
          console.log(`Export as ${exportType} not implemented yet`);
      }
    }
    setShowExportMenu(false);
  };

  const exportAsImage = async (viz: TableauVizExtended): Promise<void> => {
    try {
      const imageData = await viz.exportImageAsync();
      const link = document.createElement('a');
      link.href = imageData;
      link.download = 'dashboard_export.png';
      link.click();
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      NotificationManager.success("Image export successful!")

    //  setSnackbarMessage('Image export successful!');
    } catch (error) {
      console.error('Error exporting image:', error);
       // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      NotificationManager.error("Error exporting image!")

     // setSnackbarMessage('Error exporting image!');
    }
  };

  const exportAsPDF = async (viz: TableauViz): Promise<void> => {
    try {
      await viz.exportPDFAsync();
     // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      NotificationManager.success("PDF export successful!")
   //  setSnackbarMessage('PDF export successful!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
       // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      NotificationManager.error("Error exporting PDF!")

     // setSnackbarMessage('Error exporting PDF!');
    }
  };
  const clearFieldFilter = async (fieldName: string): Promise<void> => {
    setIsLoading(true);

    try {
      setAppliedFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters[fieldName];
        return newFilters;
      });
      setFilterValues((prev) => {
        const newValues = { ...prev };
        delete newValues[fieldName];
        return newValues;
      });

      if (vizRef.current) {
        const viz = vizRef.current.querySelector('tableau-viz') as unknown as {
          workbook: {
            activeSheet: {
              worksheets: {
                clearFilterAsync: (fieldName: string) => Promise<void>;
              }[];
            };
          };
        } | null;

        if (viz) {
          const { workbook } = viz;
          const { activeSheet } = workbook;
          const sheets = activeSheet.worksheets;

          await sheets[0].clearFilterAsync(fieldName);
           // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          void logSearchActions('Clear Applied Field',fieldName ); // To log the cleared field name from applied filters
        }
      }
    } catch (error) {
      console.error('Error clearing field filter:', error);
     // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      NotificationManager.error("Error clearing field filter!")

     // setSnackbarMessage('Error clearing field filter!');
    } finally {
      setIsLoading(false);
    }
  };
  const exportAsCrossTab = async (viz: TableauViz): Promise<void> => {
    try {
      const workbook = viz.workbook;
      const activeSheet = workbook.activeSheet;

      if (activeSheet.sheetType === 'dashboard') {
        const dashboard = activeSheet as Dashboard;
        const worksheets = dashboard.worksheets;
        const names = worksheets.map((worksheet) => worksheet.name);
        setWorksheetNames(names);
        setShowWorksheetModal(true);
      } else {
        await viz.exportCrosstabAsync(
          activeSheet.name,
          CrosstabFileFormat.Excel,
        );
         // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    NotificationManager.success("CrossTab export successful!")

      //  setSnackbarMessage('CrossTab export successful!');
      }
    } catch (error) {
      console.error('Error exporting crosstab data:', error);
       // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      NotificationManager.error("Error exporting crosstab data!")

     // setSnackbarMessage('Error exporting crosstab data!');
    }
  };

  const handleExportClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    setAnchorEl(event.currentTarget);
    setShowExportMenu(!showExportMenu);
  };

  const handleExportClose = (): void => {
    setShowExportMenu(false);
    setAnchorEl(null);
  };

  const handleWorksheetExport = async (
    selectedSheets: string[],
  ): Promise<void> => {
    if (vizRef.current) {
      const viz = vizRef.current.querySelector(
        'tableau-viz',
      ) as unknown as TableauViz;
      try {
        for (const sheet of selectedSheets) {
          await viz.exportCrosstabAsync(sheet, CrosstabFileFormat.Excel);
          console.log(`Successfully initiated export for worksheet: ${sheet}`);
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        NotificationManager.success("Worksheet exported successfully")

      //  setSnackbarMessage('Worksheet exported successfully');
      } catch (error) {
        console.error('Error exporting worksheets:', error);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        NotificationManager.error("Error exporting worksheets!")

     //   setSnackbarMessage('Error exporting worksheets!');
      }
    }
    setShowWorksheetModal(false);
  };


  const removeFilter = async (
    fieldName: string,
    value: string,
  ): Promise<void> => {
    setIsLoading(true);

    try {
      const updatedValues = appliedFilters[fieldName].filter(
        (v) => v !== value,
      );
      setAppliedFilters((prev) => ({
        ...prev,
        [fieldName]: updatedValues,
      }));
      setFilterValues((prev) => ({
        ...prev,
        [fieldName]: updatedValues,
      }));

      if (vizRef.current) {
        const viz = vizRef.current.querySelector('tableau-viz') as unknown as {
          workbook: {
            activeSheet: {
              worksheets: {
                applyFilterAsync: (
                  fieldName: string,
                  values: string[],
                  updateType: FilterUpdateType,
                ) => Promise<void>;
              }[];
            };
          };
        } | null;

        if (viz) {
          const { workbook } = viz;
          const { activeSheet } = workbook;
          const sheets = activeSheet.worksheets;

                await sheets[0].applyFilterAsync(
                    fieldName,
                    updatedValues.length > 0 ? updatedValues : [], // Apply the remaining values or no values to remove the filter
                    FilterUpdateType.Replace,
                );
            }
            const filterRemoved = {
              [fieldName]: [value]
          };
    
          void logSearchActions('Clear Applied Filter value', filterRemoved);
        }
    } catch (error) {
      console.error('Error removing filter:', error);
     // setSnackbarMessage('Error removing filter!');
     // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      NotificationManager.error("Error removing filter!");
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="relative">
      <CommonModal
        open={showWorksheetModal}
        onClose={() => setShowWorksheetModal(false)}
        worksheets={worksheetNames}
        title="Select sheets from this dashboard"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onExport={handleWorksheetExport}
      />
      <SaveReportModal
        open={showSaveFilterModal}
        onClose={() => setShowSaveFilterModal(false)}
        title="Save Reports"
        onSave={(reportName: string) => {
          void saveFilters(reportName);
        }}
        isSaveFilter={true}
        existingReportNames={existingReportNames}
      />
      <div className="flex justify-between items-center">
        <div className="flex justify-between items-center mb-[20px]">
          <div>
            <h1 className="text-2xl text-title font-semibold">Dashboard</h1>
          </div>
        </div>

        <div className="flex items-center">
          <OutlineButton
            color="gray"
            onClick={handleAddFilterClick}
            width="120px"
            disabled={isLoading}
          >
            <span
              style={{
                width: '20px',
                height: '20px',

                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '50px',
                marginRight: '5px',
              }}
            >
              <FilterAltOutlinedIcon />
            </span>
            Filters
          </OutlineButton>

          <Box sx={{ mr: 1, ml: 1 }}>
            <GradientButton
              color="red"
              width="130px"
              onClick={handleSaveFilters}
              disabled= {isLoading || !Object.values(appliedFilters).some(values => values.length > 0)}
            >
              Save
            </GradientButton>
          </Box>
          <Button1
            onClick={handleExportClick}
            variant="outlined"
            style={{
              backgroundColor: '#f0f0f0',
              borderColor: '#d0d0d0',
              color: '#000',
              borderRadius: '24px',
              minWidth: 'auto',
              display: 'flex',
              marginLeft: '10px',
            }}
            className="focus:outline-none"
          >
            <IosShareRoundedIcon />
          </Button1>

          {showExportMenu && (
            <ExportMenu
              onExport={handleExport}
              onClose={handleExportClose}
              anchorEl={anchorEl}
            />
          )}
        </div>
      </div>
      {!isLoading && Object.values(appliedFilters).some(values => values.length > 0) && (
        <AppliedFilters
          appliedFilters={appliedFilters}
          removeFilter={(fieldName, value) => {
            void removeFilter(fieldName, value);
          }}
          clearAllFilters={() => {
            void clearFilters();
          }}
          isLoading={isLoading}
          clearFieldFilter={(fieldName) => {
            void clearFieldFilter(fieldName);
          }}
        />
      )}

      <div
        className="bg-white rounded-lg p-[20px] overflow-auto relative mb-[30px]"
        style={{ minHeight: '500px' }}
      >
        {isLoading && <Loader />}
        <div ref={vizRef} className="mt-4" />
      </div>

      <FilterDrawer
        isFilterDrawerOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        filters={filters}
        filterValues={filterValues}
        filterSearch={filterSearch}
        setFilterSearch={setFilterSearch}
        handleFilterChange={handleFilterChange}
        applyFilters={applyFilters}
        clearFilters={clearFilters}
      />

      
    </div>
  );
};

export default TableauEmbedded;
